<script lang="ts" setup>
import union from 'lodash.union';
import {
  defineOgImageComponent,
  useAsyncData,
  useAuth,
  useDialog,
  useHead,
  useLocalePath,
  useSupabaseClient,
  useToast,
} from '#imports';
const { showError } = useToast();
const { currentUser } = useAuth();
const { openDialog } = useDialog();
const localePath = useLocalePath();
const { data: destinations } = await useAsyncData('destinations', async () => {
  const { data: maps, error: mapsError } = await useSupabaseClient()
    .from('combined_search')
    .select('city_ids')
    .eq('type', 'map')
    .order('children_count', { ascending: false });

  if (mapsError) showError(t('messages.maps.error-loading'));

  const flattenCities = maps.reduce((acc, item) => {
    return acc.concat(item.city_ids.filter((id) => id !== null));
  }, []);

  const { data, error } = await useSupabaseClient()
    .from('cities')
    .select('*')
    .in('id', flattenCities)
    .neq('cover_image_url', 'null')

  if (error) showError(t('messages.maps.error-loading'));
  const sortedDestinations = [...data].sort((a, b) => {
    return flattenCities.indexOf(a.id) - flattenCities.indexOf(b.id);
  });

  return sortedDestinations;
});

const subscribeWithDiscount = async () => {
  if (window) {
    window.open(
      'https://buy.stripe.com/aEU6oBdZEavx4HC4gj?prefilled_promo_code=MAPPR10',
      '_blank',
    );
  }
};

useHead(() => ({
  title: 'Discover destinations',
  meta: [
    {
      name: 'description',
      content: 'Explore the world and discover new places to visit',
    },
  ],
}));

defineOgImageComponent('Default', {
  title: 'Discover destinations',
  description: 'Explore the world and discover new places to visit',
  // imagePath: destinations.value[0].cover_image_url,
});
</script>

<template>
  <div class="destinations-page">
    <MPSectionHeader
      title="Destinations"
      subtitle="Explore your next destination"
      summary="Explore the world and discover new places to visit"
      size="large"
    />
    <div class="destinations-list">
      <div
        v-for="destination in destinations"
        :key="destination.id"
        class="destinations-item"
        :style="`background-image: url(${destination.cover_image_url})`"
      >
        <NuxtLink :to="localePath(`/destinations/${destination.id}`)">
          <div class="destinations-item-content">
            <h3>{{ destination.name }}</h3>
            <small>{{ destination.country }}</small>
          </div>
        </NuxtLink>
      </div>
      <!-- <div class="destinations-item callout">
        <div class="destinations-item-content">
          <span>
            <h3>Looking for more?</h3>
            <p>
              Explore unique experiences with 10% off Mappr Premium.
            </p>
          </span>
          <MPButton label="Get your 10% discount" version="primary" @click="currentUser ? subscribeWithDiscount() : openDialog" />
        </div>
      </div> -->
    </div>
  </div>
</template>

<style lang="postcss" scoped>
@import '@/assets/css/breakpoints.pcss';
@import '@/assets/css/typography.pcss';
@import '@/assets/css/layouts.pcss';

.destinations-page {
  @mixin layout-wrapper-wide;
  padding: 1rem 0;
  @mixin screen md {
    padding-top: 4rem;
  }
}

.destinations-list {
  display: grid;
  width: 100%;
  grid-template-columns: 1fr;
  gap: 1rem;
  /* padding: 0 1rem; */
  grid-auto-flow: dense;
  /* margin: 4rem auto; */
  padding: 1rem;
  @mixin screen sm {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-auto-flow: dense;
    > :nth-child(6n+3),
    > :nth-child(8n+5) {
      grid-column: span 2;
    }
  }
  @mixin screen md {
    display: grid;
    padding: 0;
    grid-template-columns: repeat(3, 1fr);
    grid-auto-flow: dense;
    > :nth-child(6n+3),
    > :nth-child(8n+5) {
      grid-column: span 2;
    }
  }
  @mixin screen lg {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-auto-flow: dense;
    > :nth-child(6n+3),
    > :nth-child(8n+5) {
      grid-column: span 2;
    }
  }
}
.destinations-item {
  height: 12rem;
  border-radius: 1rem;
  background-position: center;
  background-size: cover;
  color: var(--color-slate-50);
  background-color: var(--color-slate-500);
  @mixin screen sm {
    height: 18rem;
  }
}
.destinations-item-content {
  text-align: left;
  border-radius: 1rem;
  display: flex;
  flex-direction: column;
  align-items: bottom;
  padding: 1rem;
  width: 100%;
  height: 100%;
  background: linear-gradient(-20deg, rgba(0, 0, 0, 0) 30%, rgba(0, 0, 0, 0.7) 100%);
  h3 {
    margin: 0;
    @mixin typeface-h5;
  }
  small {
    color: var(--color-slate-200);
  }
}

.callout {
  display: grid;
  background-color: var(--color-green-500);
  background-color: var(--color-slate-100);
  color: var(--color-slate-900);
  border: 1px solid var(--color-slate-200);
  grid-column: 1 / span 1 !important;
  grid-row-start: 3;
  @mixin screen sm {
    grid-column: 2 / span 1 !important;
  }
  @mixin screen lg {
    grid-column: 4 / span 1 !important;
  }
  .destinations-item-content {
    justify-content: space-between;
    background-image: none;
    padding: 1.5rem;
    h5 {
      margin: 0;
    }
    p {
      color: var(--color-slate-500);
    }
  }
  /* .callout + .callout {
    grid-row-start: 12;
    @mixin screen lg {
      grid-row-start: 5;
      grid-column-start: 1;
    }
  } */
}
</style>
